import React, { useState, useEffect } from "react";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Drawer from "@mui/material/Drawer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Card from "@mui/material/Card";
import { auth, firestore } from "firebaseConfig";
import { signOut, onAuthStateChanged, updateProfile, updatePassword, deleteUser } from "firebase/auth";
import { collection, getDocs, deleteDoc, doc, updateDoc, onSnapshot } from "firebase/firestore";
import { useMaterialUIController, setOpenAccountMenu } from "context";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";

// Serviços Stripe
import { createCheckoutSession, cancelAtPeriodEnd } from "services";

// (opcional) Rótulos exibidos no modal de planos
const PRICE_MENSAL_LABEL = process.env.REACT_APP_STRIPE_PRICE_MENSAL_LABEL || "R$ 14,90 / mês";
const PRICE_ANUAL_LABEL = process.env.REACT_APP_STRIPE_PRICE_ANUAL_LABEL || "R$ 154,80 / ano";

// IDs de preço (evita typos no onClick)
const PRICE_MENSAL_ID = process.env.REACT_APP_STRIPE_PRICE_MENSAL;
const PRICE_ANUAL_ID = process.env.REACT_APP_STRIPE_PRICE_ANUAL;

function AccountMenu() {
  const [controller, dispatch] = useMaterialUIController();
  const { openAccountMenu, darkMode } = controller;
  const navigate = useNavigate();

  // Modais & campos
  const [openEdit, setOpenEdit] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [openPlanModal, setOpenPlanModal] = useState(false);

  // Dados do usuário
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Assinatura
  const [perfil, setPerfil] = useState(null);
  const [planLabel, setPlanLabel] = useState("Sem plano");
  const [statusLabel, setStatusLabel] = useState("sem assinatura");
  const [cancelLoading, setCancelLoading] = useState(false);

  // Observa auth + doc do usuário
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) return;

      setUserName(user.displayName || user.email.split("@")[0]);
      setUserEmail(user.email);

      const ref = doc(firestore, "usuarios", user.uid);
      const unsubDoc = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const d = snap.data();

        setPerfil({ id: snap.id, ...d });

        const plan = d?.plan === "mensal" ? "Mensal" : d?.plan === "anual" ? "Anual" : d?.plan || "Sem plano";
        setPlanLabel(plan);

        let status = d?.subscriptionStatus || "sem assinatura";
        const isActiveNow = status === "active" || status === "trialing";
        if (isActiveNow && d?.cancelAtPeriodEnd) status = "cancelado (acesso até o fim do período)";
        if (!d?.stripeSubscriptionId && d?.freeUntil && !d?.isBlocked) status = "em teste (7 dias)";
        setStatusLabel(status);
      });

      return () => unsubDoc();
    });

    return () => unsubAuth && unsubAuth();
  }, []);

  const handleCloseAccountMenu = () => setOpenAccountMenu(dispatch, false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/authentication/sign-in");
    } catch (error) {
      alert("Erro ao sair da conta.");
    }
  };

  const handleSaveEdit = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = doc(firestore, "usuarios", user.uid);
        if (newName) {
          await updateProfile(user, { displayName: newName });
          await updateDoc(userDoc, { nome: newName });
          setUserName(newName);
        }
        if (newPassword) await updatePassword(user, newPassword);
        alert("Dados atualizados com sucesso!");
      }
    } catch (error) {
      alert("Erro ao atualizar os dados.");
    }
    setOpenEdit(false);
  };

  const handleResetAccount = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const collectionsToClear = ["metas", "lancamentos", "categorias"];
        for (const col of collectionsToClear) {
          const qs = await getDocs(collection(firestore, `usuarios/${user.uid}/${col}`));
          for (const docSnap of qs.docs) {
            await deleteDoc(doc(firestore, `usuarios/${user.uid}/${col}`, docSnap.id));
          }
        }
        alert("Conta resetada com sucesso!");
      }
    } catch (error) {
      alert("Erro ao resetar a conta.");
    }
    setOpenReset(false);
  };

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await deleteUser(user);
        await signOut(auth);
        navigate("/authentication/sign-in");
      }
    } catch (error) {
      alert("Erro ao excluir a conta.");
    }
    setOpenDelete(false);
  };

  // Stripe
  const handleSubscribe = async (priceId) => {
    try {
      if (!priceId || !String(priceId).startsWith("price_")) {
        alert("Configuração de preço ausente ou inválida. Verifique as variáveis de ambiente.");
        return;
      }
      const user = auth.currentUser;
      if (!user) return alert("Você precisa estar logado.");
      setOpenPlanModal(false); // fecha o modal antes de redirecionar
      await createCheckoutSession({ uid: user.uid, priceId });
    } catch (err) {
      alert("Erro ao iniciar assinatura: " + err.message);
    }
  };

  const handleCancelPlan = async () => {
    if (!perfil?.stripeSubscriptionId) return alert("Assinatura não localizada.");
    const ok = window.confirm(
      `Cancelar plano agora? Você manterá acesso até ${
        perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "o fim do período atual"
      }.`
    );
    if (!ok) return;
    try {
      setCancelLoading(true);
      await cancelAtPeriodEnd(perfil.stripeSubscriptionId);
      alert("Cancelamento agendado com sucesso.");
    } catch (e) {
      alert("Não foi possível cancelar: " + e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  // Condições
  const isActive = perfil?.subscriptionStatus === "active" || perfil?.subscriptionStatus === "trialing";
  const canShowCancelBtn = isActive && !perfil?.cancelAtPeriodEnd && !!perfil?.stripeSubscriptionId;

  // mostra "Assinar" se não tiver sub ativa, estiver cancelado, sem status, ou até mesmo em trial
  const canShowSubscribeBtn =
    !perfil?.stripeSubscriptionId ||
    perfil?.subscriptionStatus === "trialing" ||
    perfil?.subscriptionStatus === "canceled" ||
    !perfil?.subscriptionStatus;

  // Drawer
  const drawerStyles = {
    "& .MuiDrawer-paper": {
      height: "100vh",
      margin: 0,
      padding: 0,
      width: openAccountMenu ? 350 : 0,
      background: darkMode ? "linear-gradient(195deg, #42424a, #191919)" : "#ffffff",
      boxShadow: openAccountMenu ? "0 8px 32px rgba(0, 0, 0, 0.2)" : "none",
      border: "none",
      overflowX: "hidden",
      transition: "width 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
      position: "fixed",
      top: 0,
      right: 0,
      visibility: openAccountMenu ? "visible" : "hidden",
      zIndex: 1300,
    },
  };

  return (
    <>
      <Drawer variant="permanent" anchor="right" open={openAccountMenu} sx={drawerStyles}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} px={3}>
          <MDTypography variant="h5" fontWeight="medium" color={darkMode ? "white" : "dark"}>
            Minha Conta
          </MDTypography>
          <Icon
            sx={({ typography: { size }, palette: { dark, white } }) => ({
              fontSize: `${size.lg} !important`,
              color: darkMode ? white.main : dark.main,
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
            })}
            onClick={handleCloseAccountMenu}
          >
            close
          </Icon>
        </MDBox>

        <Divider sx={{ my: 2 }} />

        <MDBox pt={1} pb={3} px={3}>
          {/* Dados do usuário */}
          <MDBox
            mb={3}
            p={2}
            sx={{
              backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
              borderRadius: "12px",
              boxShadow: darkMode ? "0 2px 8px rgba(255, 255, 255, 0.1)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={darkMode ? "white" : "dark"}
              mb={1}
              display="block"
            >
              Nome Completo
            </MDTypography>
            <TextField
              value={userName}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                sx: { backgroundColor: darkMode ? "#2a2a2a" : "#f9f9f9", borderRadius: "8px" },
              }}
              sx={{ mb: 2 }}
            />
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={darkMode ? "white" : "dark"}
              mb={1}
              display="block"
            >
              E-mail
            </MDTypography>
            <TextField
              value={userEmail}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                sx: { backgroundColor: darkMode ? "#2a2a2a" : "#f9f9f9", borderRadius: "8px" },
              }}
            />
          </MDBox>

          {/* Ações da Conta */}
          <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
            <MDButton
              onClick={() => setOpenEdit(true)}
              color="info"
              variant="contained"
              sx={{ flex: "1 1 48%", borderRadius: "10px", textTransform: "none", fontWeight: "medium" }}
            >
              Editar Conta
            </MDButton>
            <MDButton
              onClick={() => setOpenReset(true)}
              color="warning"
              variant="contained"
              sx={{ flex: "1 1 48%", borderRadius: "10px", textTransform: "none", fontWeight: "medium" }}
            >
              Resetar Conta
            </MDButton>
            <MDButton
              onClick={() => setOpenDelete(true)}
              color="error"
              variant="outlined"
              sx={{ flex: "1 1 48%", borderRadius: "10px", textTransform: "none", fontWeight: "medium" }}
            >
              Excluir Conta
            </MDButton>
            <MDButton
              onClick={() => setOpenSupport(true)}
              color="primary"
              variant="outlined"
              sx={{ flex: "1 1 48%", borderRadius: "10px", textTransform: "none", fontWeight: "medium" }}
            >
              Contato com Suporte
            </MDButton>
          </MDBox>

          {/* Assinatura */}
          <MDBox
            p={2}
            sx={{
              backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
              borderRadius: "12px",
              boxShadow: darkMode ? "0 2px 8px rgba(255, 255, 255, 0.1)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={darkMode ? "white" : "dark"}
              mb={1}
              display="block"
            >
              Assinatura
            </MDTypography>

            <MDTypography variant="body2" color={darkMode ? "white" : "dark"}>
              Plano Atual: {planLabel}
            </MDTypography>

            <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ display: "block", mt: 0.5 }}>
              {(() => {
                if (!perfil) return "Status: -";
                if (isActive && !perfil?.cancelAtPeriodEnd) {
                  return `Status: Ativo · renova em ${
                    perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "-"
                  }`;
                }
                if (isActive && perfil?.cancelAtPeriodEnd) {
                  return `Status: Cancelado · acesso até ${
                    perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "-"
                  }`;
                }
                if (!perfil?.stripeSubscriptionId && perfil?.freeUntil && !perfil?.isBlocked) {
                  return `Status: Em teste · expira em ${perfil?.freeUntil?.toDate?.().toLocaleDateString?.() || "-"}`;
                }
                return `Status: ${statusLabel}`;
              })()}
            </MDTypography>

            {/* Botões de ação da assinatura */}
            {canShowSubscribeBtn && (
              <MDBox mt={2}>
                <MDButton fullWidth color="info" onClick={() => setOpenPlanModal(true)}>
                  Assinar
                </MDButton>
              </MDBox>
            )}

            {canShowCancelBtn && (
              <MDBox mt={2}>
                <MDButton
                  fullWidth
                  color="error"
                  variant="outlined"
                  onClick={handleCancelPlan}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? "Cancelando..." : "Cancelar plano"}
                </MDButton>
              </MDBox>
            )}
          </MDBox>

          <Divider sx={{ my: 3 }} />

          {/* Sair */}
          <MDBox display="flex" justifyContent="center">
            <MDButton
              onClick={handleLogout}
              color="error"
              variant="contained"
              startIcon={<LogoutIcon />}
              sx={{
                borderRadius: "10px",
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "medium",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                "&:hover": { boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)" },
              }}
            >
              Sair
            </MDButton>
          </MDBox>

          {/* Rodapé */}
          <MDBox mt={4} textAlign="center">
            <MDTypography
              variant="caption"
              color={darkMode ? "white" : "text"}
              sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              © 2024 Finnoplan. Todos os direitos reservados. <br />
              Versão 2.0.0
            </MDTypography>
          </MDBox>
        </MDBox>
      </Drawer>

      {/* Modal: Editar conta */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Editar Conta</DialogTitle>
        <DialogContent>
          <TextField label="Novo Nome" fullWidth margin="normal" onChange={(e) => setNewName(e.target.value)} />
          <TextField
            label="Nova Senha"
            type="password"
            fullWidth
            margin="normal"
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleSaveEdit} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal: Resetar conta */}
      <Dialog
        open={openReset}
        onClose={() => setOpenReset(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Resetar Conta</DialogTitle>
        <DialogContent>Tem certeza que deseja resetar sua conta? Todos os dados serão apagados.</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenReset(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleResetAccount} color="warning">
            Resetar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal: Excluir conta */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Excluir Conta</DialogTitle>
        <DialogContent>Tem certeza que deseja excluir sua conta permanentemente?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDelete(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleDeleteAccount} color="error">
            Excluir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal: Suporte */}
      <Dialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Contato com Suporte</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">Para entrar em contato com o suporte, envie um e-mail para:</MDTypography>
          <MDTypography variant="h6" color="info">
            infofinnoplan@gmail.com
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenSupport(false)} color="primary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal: Escolher plano (único botão "Assinar") */}
      <Dialog
        open={openPlanModal}
        onClose={() => setOpenPlanModal(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            background: darkMode ? "linear-gradient(180deg, #1f1f1f 0%, #141414 100%)" : "#fff",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, pt: 3 }}>Escolha seu plano</DialogTitle>
        <DialogContent sx={{ px: 2, pb: 1 }}>
          <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
            <Card
              onClick={() => handleSubscribe(PRICE_MENSAL_ID)}
              sx={{
                p: 2,
                cursor: "pointer",
                borderRadius: "14px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                transition: "transform 120ms ease, box-shadow 120ms ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 28px rgba(0,0,0,0.12)" },
                background: darkMode ? "#232323" : "#f9fafb",
              }}
            >
              <MDTypography variant="overline" color="info" fontWeight="bold">
                Mensal
              </MDTypography>
              <MDTypography variant="h5" mt={0.5} mb={1}>
                {PRICE_MENSAL_LABEL}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Acesso completo, renovação mensal.
              </MDTypography>
            </Card>

            <Card
              onClick={() => handleSubscribe(PRICE_ANUAL_ID)}
              sx={{
                p: 2,
                cursor: "pointer",
                borderRadius: "14px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                transition: "transform 120ms ease, box-shadow 120ms ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 28px rgba(0,0,0,0.12)" },
                background: darkMode ? "#232323" : "#f9fafb",
                border: "1px solid rgba(34,197,94,0.35)",
              }}
            >
              <MDTypography variant="overline" color="success" fontWeight="bold">
                Anual
              </MDTypography>
              <MDTypography variant="h5" mt={0.5} mb={1}>
                {PRICE_ANUAL_LABEL}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                12 meses de acesso. Economia no ano.
              </MDTypography>
            </Card>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <MDButton fullWidth color="secondary" variant="outlined" onClick={() => setOpenPlanModal(false)}>
            Voltar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AccountMenu;
